import { useQuery } from '@tanstack/react-query';
import { Database, CheckCircle, XCircle, Activity, Clock } from 'lucide-react';
import { api, DashboardStats } from '../services/api';

export default function DataSourcesPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['data-sources'],
    queryFn: api.getDataSources,
  });

  const { data: stats } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: api.getDashboardStats,
  });

  const { data: ingestionData } = useQuery({
    queryKey: ['ingestion-jobs'],
    queryFn: () => api.getIngestionJobs({ limit: 100 }),
  });

  // Get last ingestion date per source
  const lastUpdatedBySource: Record<string, string> = {};
  if (ingestionData?.jobs) {
    for (const job of ingestionData.jobs) {
      if (job.status === 'completed' && job.completedAt) {
        const source = job.dataSource;
        if (!lastUpdatedBySource[source] || new Date(job.completedAt) > new Date(lastUpdatedBySource[source])) {
          lastUpdatedBySource[source] = job.completedAt;
        }
      }
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-400">
        Error loading data sources.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Data Sources</h1>
      </div>

      {/* System Status */}
      <div className="card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">System Status</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${stats?.systemStatus?.senzingApiHealthy ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-gray-700 dark:text-gray-300">MERLIN Engine</span>
            </div>
            <span className={`text-sm font-medium ${stats?.systemStatus?.senzingApiHealthy ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {stats?.systemStatus?.senzingApiHealthy ? 'Healthy' : 'Unhealthy'}
            </span>
          </div>
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${stats?.systemStatus?.databaseConnected ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-gray-700 dark:text-gray-300">Database Connection</span>
            </div>
            <span className={`text-sm font-medium ${stats?.systemStatus?.databaseConnected ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {stats?.systemStatus?.databaseConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
        </div>
      </div>

      {/* MERLIN Data Sources */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">MERLIN Configured Sources</h2>
        {data?.senzingDataSources && data.senzingDataSources.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {data.senzingDataSources.map((source) => (
              <span key={source} className="badge-blue flex items-center gap-1">
                <Database className="h-3 w-3" />
                {source}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400">No data sources configured in MERLIN yet.</p>
        )}
      </div>

      {/* Application Data Sources */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Configured Data Sources</h2>

        {data?.dataSources && data.dataSources.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Jurisdiction</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Last Updated</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {data.dataSources.map((source) => (
                  <tr key={source.code} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-gray-900 dark:text-white">
                        {source.code}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{source.displayName}</td>
                    <td className="px-4 py-3">
                      <SourceTypeBadge type={source.sourceType} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{source.jurisdiction || '-'}</td>
                    <td className="px-4 py-3">
                      {lastUpdatedBySource[source.code] ? (
                        <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                          <Clock className="h-3 w-3" />
                          {new Date(lastUpdatedBySource[source.code]).toLocaleDateString()}
                          <span className="text-gray-400 dark:text-gray-500">
                            {new Date(lastUpdatedBySource[source.code]).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {source.isActive ? (
                        <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                          <CheckCircle className="h-4 w-4" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-gray-400">
                          <XCircle className="h-4 w-4" />
                          Inactive
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <Database className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">No data sources configured yet.</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Data sources are created automatically when you upload and process files.
            </p>
          </div>
        )}
      </div>

      {/* Predefined Sources Info */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Available Source Types</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SourceTypeCard
            type="LOYALTY"
            title="Loyalty Program"
            description="Customer loyalty program records with patron information"
          />
          <SourceTypeCard
            type="EXCLUSION"
            title="Exclusion List"
            description="Lists of individuals excluded from programs or services"
          />
          <SourceTypeCard
            type="REGULATORY"
            title="Regulatory List"
            description="Government or regulatory watchlists and compliance lists"
          />
        </div>
      </div>
    </div>
  );
}

function SourceTypeBadge({ type }: { type: string }) {
  const classes: Record<string, string> = {
    LOYALTY: 'badge-blue',
    EXCLUSION: 'badge-red',
    REGULATORY: 'badge-yellow',
    CUSTOMER: 'badge-green',
    OTHER: 'badge-gray',
  };
  return <span className={classes[type] || 'badge-gray'}>{type}</span>;
}

function SourceTypeCard({ type, title, description }: { type: string; title: string; description: string }) {
  const colorClasses: Record<string, string> = {
    LOYALTY: 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20',
    EXCLUSION: 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20',
    REGULATORY: 'border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20',
  };

  return (
    <div className={`p-4 rounded-lg border ${colorClasses[type] || 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'}`}>
      <div className="flex items-center gap-2 mb-2">
        <SourceTypeBadge type={type} />
      </div>
      <h3 className="font-medium text-gray-900 dark:text-white">{title}</h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{description}</p>
    </div>
  );
}
